import { ScrollView } from 'react-native';
import { Chip, Flex, IconChevronDown } from '@banhala/ui-kit';
import { useAtom } from 'jotai';
import { useBottomSheetRoute, useIsInFragmentScreens } from 'shared/src/hooks';
import { useLikedGoodsCategories } from '../../../queries';
import { likeGoodsFilterAtom } from '../../../states/likeGoodsFilter.atom';
import {
  DEFAULT_SORT_TYPE_IN_LIKE,
  SORTING_TYPE_IN_LIKE,
  type SortTypeInLike,
} from '../constants/sortingTypeInLike';
import { useLogSortAndCategory } from '../hooks/useLogSortAndCategory';
import { useSortSelectBottomSheet } from './SortSelectBottomSheet';

const ALL_CATEGORY_SNO = -1;

interface CategoryFilterRowProps {
  /** 로딩 중일 때 칩 탭 막아 race 방지. iOS 의 loadingIndicator 차단과 동일 의도. */
  disabled?: boolean;
}

/**
 * iOS `LikedProductCategoryFilterCell` 대응.
 * 정렬 칩(드롭다운) + 카테고리 칩 가로 스크롤.
 */
export const CategoryFilterRow = ({
  disabled = false,
}: CategoryFilterRowProps = {}) => {
  const { data: categories } = useLikedGoodsCategories();
  const [filter, setFilter] = useAtom(likeGoodsFilterAtom);
  const { show: showSortSheet } = useSortSelectBottomSheet();
  const { open: openBottomSheetRoute } = useBottomSheetRoute();
  const isInFragmentScreens = useIsInFragmentScreens();
  const { logClickSort, logApplySort, logApplyCategory } =
    useLogSortAndCategory();

  const selectedSno = filter.selectedCategorySno ?? ALL_CATEGORY_SNO;

  const selectedSort =
    SORTING_TYPE_IN_LIKE.find(sort => sort.key === filter.sortKey) ??
    DEFAULT_SORT_TYPE_IN_LIKE;
  const isCustomSort = filter.sortKey !== DEFAULT_SORT_TYPE_IN_LIKE.key;

  // 현재 선택된 카테고리 객체 (sort/category 로깅 시 함께 전달)
  const currentCategory = categories.find(
    c => c.sno === filter.selectedCategorySno,
  );

  const handlePressCategory = (sno: number) => {
    if (disabled) return;
    if (selectedSno === sno) return;
    const nextCategory =
      sno === ALL_CATEGORY_SNO
        ? undefined
        : categories.find(c => c.sno === sno);
    logApplyCategory({ category: nextCategory });
    setFilter(prev => ({
      ...prev,
      selectedCategorySno: sno === ALL_CATEGORY_SNO ? null : sno,
    }));
  };

  const handlePressSort = async () => {
    if (disabled) return;
    logClickSort({
      sortType: filter.sortKey,
      category: currentCategory,
    });
    const result = isInFragmentScreens
      ? await openBottomSheetRoute<
          { selectedKey: string },
          { nextValue: SortTypeInLike }
        >('sort-select-in-like', { selectedKey: filter.sortKey })
      : await showSortSheet({ selectedKey: filter.sortKey });
    if (result.type !== 'confirm') return;
    logApplySort({
      sortType: result.data.nextValue.key,
      category: currentCategory,
    });
    setFilter(prev => ({ ...prev, sortKey: result.data.nextValue.key }));
  };

  return (
    <Flex backgroundColor={'bg_primary'}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingVertical: 12,
          gap: 8,
        }}
      >
        <Chip
          // 기본 정렬에서 벗어났을 때만 active 표시 (iOS 동일)
          state={
            disabled
              ? 'disabled'
              : isCustomSort
                ? 'selected'
                : 'enabled'
          }
          size={'small'}
          rightIcon={<IconChevronDown />}
          onPress={handlePressSort}
          testID={`정렬필터칩?value=${selectedSort.name}`}
        >
          {selectedSort.name}
        </Chip>
        <Chip
          state={
            disabled
              ? 'disabled'
              : selectedSno === ALL_CATEGORY_SNO
                ? 'selected'
                : 'enabled'
          }
          size={'small'}
          onPress={() => handlePressCategory(ALL_CATEGORY_SNO)}
          testID={`카테고리필터칩?index=0&value=전체`}
        >
          전체
        </Chip>
        {categories.map((category, index) => (
          <Chip
            key={category.sno}
            state={
              disabled
                ? 'disabled'
                : selectedSno === category.sno
                  ? 'selected'
                  : 'enabled'
            }
            size={'small'}
            onPress={() => handlePressCategory(category.sno)}
            testID={`카테고리필터칩?index=${index + 1}&value=${category.name}`}
          >
            {category.name}
          </Chip>
        ))}
      </ScrollView>
    </Flex>
  );
};
